/**
 * Customer renderer for tenant-tunable B2C options (the ``widget:"tunable"``
 * presets from ``b2c_presets_schema``, merged across capable machines by
 * ``/pricing/feasibility``).
 *
 * Each tunable is the single owner of its customer input — it writes a
 * **numeric** value into ``config[key]`` (the same key the pricing formula
 * reads), replacing whatever static field ``configurator_schema`` declared for
 * that key. When the tenant enables ``allow_slider`` the customer gets a
 * continuous slider between the lowest and highest preset; otherwise a
 * dropdown of the tenant's presets.
 */

import type { JSX } from "react";

import { presetLabel } from "./presetLabel";

import type { TunablePreset } from "@/api/queries/pricing";

interface Props {
  tunables: Record<string, TunablePreset>;
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  lang: string;
}

function numericPresetValues(t: TunablePreset): number[] {
  return t.presets
    .map((p) => p.value)
    .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
}

/** Slider step: integers step by 1; otherwise the smallest gap between
 *  presets (so the slider snaps to the granularity the tenant offered). */
function sliderStep(values: number[], valueType?: string): number {
  if (valueType === "integer") return 1;
  const sorted = [...new Set(values)].sort((a, b) => a - b);
  let gap = Infinity;
  for (let i = 1; i < sorted.length; i++) gap = Math.min(gap, sorted[i] - sorted[i - 1]);
  return Number.isFinite(gap) && gap > 0 ? gap : 0.01;
}

function parseValue(raw: string, valueType?: string): number | undefined {
  if (raw === "") return undefined;
  const n = valueType === "integer" ? Number.parseInt(raw, 10) : Number.parseFloat(raw);
  return Number.isFinite(n) ? n : undefined;
}

export function TunablePresetsSection({
  tunables,
  config,
  onChange,
  lang,
}: Props): JSX.Element | null {
  const keys = Object.keys(tunables);
  if (keys.length === 0) return null;

  const set = (key: string, value: unknown) => onChange({ ...config, [key]: value });

  return (
    <div className="flex flex-col gap-3" data-testid="tunable-presets">
      {keys.map((key) => {
        const tunable = tunables[key];
        const values = numericPresetValues(tunable);
        if (values.length === 0) return null;
        const label = tunable.title || key;
        const unit = tunable.unit;
        const current = typeof config[key] === "number" ? (config[key] as number) : undefined;

        if (tunable.allow_slider) {
          const min = Math.min(...values);
          const max = Math.max(...values);
          const step = sliderStep(values, tunable.value_type);
          const safe = typeof current === "number" ? current : min;
          return (
            <label
              key={key}
              className="flex flex-col gap-1 text-[12px] text-ink-3"
              data-testid={`tunable-${key}`}
            >
              <span>
                {label}
                {unit ? ` (${unit})` : ""}
              </span>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={min}
                  max={max}
                  step={step}
                  value={safe}
                  onChange={(e) => set(key, Number(e.target.value))}
                  className="flex-1"
                  data-testid={`tunable-${key}-slider`}
                />
                <input
                  type="number"
                  min={min}
                  max={max}
                  step={step}
                  value={safe}
                  onChange={(e) => {
                    // Clearing the field must not store `undefined` (would drop
                    // the pricing key + desync the displayed `safe`); fall back
                    // to the current displayed value instead.
                    const v = parseValue(e.target.value, tunable.value_type);
                    set(key, v === undefined ? safe : v);
                  }}
                  className="h-9 w-24 rounded border border-border bg-surface px-2 text-[13px] text-ink"
                  data-testid={`tunable-${key}-number`}
                />
              </div>
            </label>
          );
        }

        // No empty "—" option: infill/layer-height are pricing-gated, so the
        // control always reflects a valid preset. `current` is kept valid by
        // the seed effect; fall back defensively for the first render.
        const fallback =
          typeof tunable.default === "number" && values.includes(tunable.default)
            ? tunable.default
            : values[0];
        const selectValue =
          typeof current === "number" && values.includes(current)
            ? String(current)
            : String(fallback);
        return (
          <label
            key={key}
            className="flex flex-col gap-1 text-[12px] text-ink-3"
            data-testid={`tunable-${key}`}
          >
            <span>
              {label}
              {unit ? ` (${unit})` : ""}
            </span>
            <select
              value={selectValue}
              onChange={(e) => {
                const v = parseValue(e.target.value, tunable.value_type);
                set(key, v === undefined ? fallback : v);
              }}
              className="h-9 rounded border border-border bg-surface px-2 text-[13px] text-ink"
              data-testid={`tunable-${key}-select`}
            >
              {tunable.presets.map((p, i) =>
                typeof p.value === "number" && Number.isFinite(p.value) ? (
                  <option key={i} value={String(p.value)}>
                    {presetLabel(p, lang, unit, values)}
                  </option>
                ) : null,
              )}
            </select>
          </label>
        );
      })}
    </div>
  );
}

export default TunablePresetsSection;
